import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, ListGroup } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import NoDataDisplay from 'components/commons/NoDataDisplay';
import TableLoader from 'components/commons/TableLoader';
import _ from 'lodash';
import BeSafeContainer from '../BeSafeContainer';
import { ExpandMore, KeyboardArrowRight } from '@material-ui/icons';
import BeSafeSearchBar from '../BeSafeSearchBar';
import BeSafeSeparator from '../BeSafeSeparator';

const BeSafeMultiSelectModal = (props) => {
  const [selectedItems, setSelectedItems] = useState(props.initialSelected);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const initialItems = props.items;

  const handleSubmit = () => {
    if (_.isFunction(props.onChange)) {
      props.onChange(selectedItems);
      onHide();
    }
  };

  const onHide = () => {
    setSelectedItems([]);
    setDisplayedItems([]);
    props.onHide();
    setSearchQuery('');
  };

  useEffect(() => {
    setLoading(true);

    if (searchQuery === '') {
      setItems(initialItems);
      setLoading(false);
      return;
    }

    const filteredItems = [];

    initialItems.forEach((group) => {
      if (hasChildren(group)) {
        filteredItems.push({
          name: group.name,
          displayName: group.displayName,
          children: group.children.filter(
            (item) =>
              item.displayName
                .toLowerCase()
                .startsWith(searchQuery.toLowerCase()) ||
              item.displayName.toLowerCase().includes(searchQuery.toLowerCase())
          ),
        });
        return;
      }

      if (
        group.displayName.toLowerCase().startsWith(searchQuery.toLowerCase()) ||
        group.displayName.toLowerCase().includes(searchQuery.toLowerCase())
      ) {
        filteredItems.push(group);
      }
    });

    setItems(filteredItems);

    setLoading(false);
  }, [searchQuery, initialItems]);

  const isSelected = (item) => {
    if (hasChildren(item)) {
      return areAllChildrenSelected(item);
    }
    return selectedItems.findIndex((name) => name === item.name) > -1;
  };

  const isChildSelected = (item) => {
    if (!hasChildren(item)) return false;

    const childrenNames = item.children?.map((child) => child.name);

    return _.intersection(childrenNames, selectedItems).length > 0;
  };

  const selectItem = (item) => {
    if (hasChildren(item)) {
      const childrenNames = item.children?.map((child) => child.name);

      if (isChildSelected(item)) {
        setSelectedItems(
          selectedItems.filter((name) =>
            !childrenNames.includes(name) ? true : false
          )
        );
        return;
      }

      setSelectedItems([...selectedItems, ...childrenNames]);
      return;
    }

    if (_.includes(selectedItems, item.name)) {
      const newItems = _.without(selectedItems, item.name);
      setSelectedItems(newItems);
    } else {
      if (props.limit === 0 || selectedItems.length < props.limit) {
        setSelectedItems([...selectedItems, item.name]);
      }
    }
  };

  const hasChildren = (item) => {
    return item.children && item.children.length > 0;
  };

  const showChildren = (item, e) => {
    e.stopPropagation();
    setDisplayedItems([...displayedItems, item.name]);
  };

  const hideChildren = (item, e) => {
    e.stopPropagation();
    setDisplayedItems(displayedItems.filter((name) => name !== item.name));
  };

  const areAllChildrenSelected = (item) => {
    if (!hasChildren(item)) return false;

    const childrenNames = item.children?.map((child) => child.name);

    return (
      _.intersection(childrenNames, selectedItems).length ===
      childrenNames.length
    );
  };

  const areChildrenPartiallySelected = (item) => {
    return isChildSelected(item) && !areAllChildrenSelected(item);
  };

  const onSearch = (query) => {
    setSearchQuery(query);
  };

  const displayChildren = (item) => {
    if (displayedItems.includes(item.name)) {
      return item.children.map((child) => (
        <React.Fragment key={`${item.name} ${child.name}`}>
          <ListGroup.Item
            className="rounded-0 d-flex"
            action
            onClick={(e) => selectItem(child)}
          >
            <div className="pl-3">
              <KeyboardArrowRight className="invisible" />
              <Form.Check
                type="checkbox"
                className="mb-0 d-inline-block"
                value={isSelected(child)}
                checked={isSelected(child)}
                onChange={(e) => selectItem(child)}
              />
              {child.displayName}
            </div>
          </ListGroup.Item>
        </React.Fragment>
      ));
    }
  };

  return (
    <>
      <Modal
        show={props.showModal}
        onEnter={() => {
          setSelectedItems(props.initialSelected);
          setItems(props.items);
        }}
        onHide={onHide}
        size="lg"
        centered
        dialogClassName="modal-shadow-lg"
      >
        {props.title ? (
          <Modal.Header closeButton>
            <Modal.Title>
              <FormattedMessage id={props.title} />
            </Modal.Title>
          </Modal.Header>
        ) : (
          <Modal.Header className="p-4 py-1 px-2">
            <BeSafeSearchBar onSearch={onSearch} />
          </Modal.Header>
        )}

        <Modal.Body className="pt-3">
          {props.title && (
            <>
              <div className="w-100 d-flex align-items-center">
                <div className="w-100">
                  <BeSafeSearchBar onSearch={onSearch} />
                </div>
              </div>
              <BeSafeSeparator />
            </>
          )}
          <TableLoader rows={8} visible={loading} />
          <NoDataDisplay visible={!loading && items.length <= 0} />
          <BeSafeContainer visible={!loading && items.length > 0}>
            <ListGroup
              variant="flush"
              className="modal-selection-list overflow-auto"
            >
              {items.map((item) => (
                <React.Fragment key={item.name}>
                  <ListGroup.Item
                    className="rounded-0 d-flex"
                    action
                    onClick={() => selectItem(item)}
                  >
                    <div>
                      {hasChildren(item) &&
                        !displayedItems.includes(item.name) && (
                          <KeyboardArrowRight
                            onClick={(e) => showChildren(item, e)}
                          />
                        )}
                      {hasChildren(item) &&
                        displayedItems.includes(item.name) && (
                          <ExpandMore onClick={(e) => hideChildren(item, e)} />
                        )}
                      {!hasChildren(item) && (
                        <KeyboardArrowRight className="invisible" />
                      )}
                      <Form.Check
                        type="checkbox"
                        className="mb-0 d-inline-block align-middle"
                        value={isSelected(item)}
                        checked={isSelected(item)}
                        onChange={() => selectItem(item)}
                        ref={(elm) => {
                          if (elm) {
                            elm.indeterminate =
                              areChildrenPartiallySelected(item);
                          }
                        }}
                      />
                      {hasChildren(item) ? (
                        <>
                          {item.displayName}
                          <span className="ml-1">({item.children.length})</span>
                        </>
                      ) : (
                        item.displayName
                      )}
                    </div>
                  </ListGroup.Item>
                  {displayChildren(item)}
                </React.Fragment>
              ))}
            </ListGroup>
          </BeSafeContainer>
        </Modal.Body>
        <Modal.Footer className="py-3">
          <Button variant="transparent" onClick={onHide}>
            <FormattedMessage id="OBJECT_MODAL.CANCEL" />
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            <i className="fas fa-check mr-2" />
            <FormattedMessage id="GENERAL.SAVE" />
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BeSafeMultiSelectModal;
